import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api-client'
import { errorMessage } from '@/lib/errors'
import type { Category } from '@/types/api'

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
    staleTime: 5 * 60_000,
  })
}

export interface CategoryInput {
  name: string
  description?: string
  parentId?: string | null
}

function useInvalidateCategories() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['categories'] })
}

export function useCreateCategory() {
  const invalidate = useInvalidateCategories()
  return useMutation({
    mutationFn: (input: CategoryInput) =>
      api.post<Category>('/categories', input),
    onSuccess: () => {
      invalidate()
      toast.success('Category created')
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not create the category.')),
  })
}

export function useUpdateCategory() {
  const invalidate = useInvalidateCategories()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CategoryInput> }) =>
      api.patch<Category>(`/categories/${id}`, input),
    onSuccess: () => {
      invalidate()
      toast.success('Category saved')
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not save the category.')),
  })
}

export function useDeleteCategory() {
  const invalidate = useInvalidateCategories()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/categories/${id}`),
    onSuccess: () => {
      invalidate()
      toast.success('Category deleted')
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not delete the category.')),
  })
}
